/**
 * Cross-Division Dependencies Widget (issue #1652, part of epic #1650)
 *
 * Shows DEPENDS_ON edges whose source and target services resolve to
 * different divisions, matching `homepage-dashboard-spec.md`'s
 * "Cross-Division Dependencies" widget. "Division" is the hierarchy root
 * resolved by walking `parentTeamId` (ADR-1254), never a `Team.division`
 * field. Administrators see every cross-division edge; other users see
 * only edges involving their own division.
 */

import { useCrossDivisionDependencies } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './CrossDivisionDependenciesWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'cross-division-dependencies',
  title: 'Cross-Division Dependencies',
  size: 'large',
  refreshInterval: 600000, // 10 minutes — matches the sibling division-scoped widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface CrossDivisionDependenciesStats {
  edges: Array<{
    fromDivision: string;
    toDivision: string;
    dependencyCount: number;
    criticalCount: number;
  }>;
  totalCrossDivisionDeps: number;
}

type CrossDivisionDependenciesQueryData = ReturnType<typeof useCrossDivisionDependencies>['data'];

function extractCrossDivisionDependenciesStats(
  data: CrossDivisionDependenciesQueryData
): CrossDivisionDependenciesStats | null {
  if (!data?.crossDivisionDependencies) {
    return null;
  }
  return data.crossDivisionDependencies;
}

export function CrossDivisionDependenciesWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useCrossDivisionDependencies(),
    extractCrossDivisionDependenciesStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="cross-division-dependencies-widget__empty">
          <IconElement name="hub" className="cross-division-dependencies-widget__empty-icon" />
          <p className="forge-typography--body2">No cross-division dependency data available</p>
        </div>
      </DashboardWidget>
    );
  }

  if (stats.edges.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="cross-division-dependencies-widget__empty">
          <IconElement
            name="check_circle"
            className="cross-division-dependencies-widget__empty-icon"
          />
          <p className="forge-typography--body2">No cross-division dependencies detected</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="cross-division-dependencies-widget__content">
        <div className="cross-division-dependencies-widget__total">
          <span className="cross-division-dependencies-widget__total-number">
            {stats.totalCrossDivisionDeps}
          </span>
          <span className="cross-division-dependencies-widget__total-label forge-typography--label2">
            Cross-Division Dependencies
          </span>
        </div>

        <ul
          className="cross-division-dependencies-widget__edge-list"
          data-testid="cross-division-dependencies-edges"
        >
          {stats.edges.map((edge) => (
            <li
              key={`${edge.fromDivision}->${edge.toDivision}`}
              className="cross-division-dependencies-widget__edge"
            >
              <span className="cross-division-dependencies-widget__edge-path forge-typography--body2">
                <span>{edge.fromDivision}</span>
                <IconElement
                  name="arrow_forward"
                  className="cross-division-dependencies-widget__edge-arrow"
                  aria-hidden="true"
                />
                <span>{edge.toDivision}</span>
              </span>
              <span className="cross-division-dependencies-widget__edge-count">
                {edge.dependencyCount}
                {edge.criticalCount > 0 && (
                  <span className="cross-division-dependencies-widget__edge-critical">
                    ({edge.criticalCount} critical)
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </DashboardWidget>
  );
}
